import { z } from "zod";

import { VibeAppSmokeRoutesReadSchema, VibeAppSmokeRoutesSchema } from "./post-deploy-smoke.schema";

/**
 * VibeApp.healthCheckConfigJson — the per-app, Porter-style health
 * probe the deployer runs against a fresh deployment before flipping
 * it HEALTHY. The executor reads these thresholds (delivered via the
 * dispatch `app` payload) to configure the probe; the tool-registration
 * gate consumes the resulting HEALTHY status.
 *
 * The Zod `.safeParse` at the entity boundary catches legacy / corrupted
 * rows and falls back to defaults rather than crashing — a row that
 * pre-dates this slice's migration default has the column backfilled by
 * the migration itself.
 */

export const VIBE_APP_HEALTH_CHECK_BOUNDS = {
  /// Request path probed over HTTP. Must be absolute. 2048 is the de-facto
  /// safe URL-path ceiling across proxies.
  path: { maxLength: 2048 },
  /// The port the app is deployed on: what its container publishes, what this
  /// probe connects to, and what Traefik routes to. `null` — the common case —
  /// is `VIBE_APP_DEFAULT_CONTAINER_PORT`, NOT the image's `EXPOSE`, which
  /// nothing at deploy time reads. The app is told the resolved value through
  /// the `PORT` env var, so setting this moves the app and the probe together.
  port: { min: 1, max: 65_535 },
  /// Grace period after a container starts before the first probe — covers
  /// app boot (DB pool warmup, migrations) without flapping. 10min ceiling.
  initialDelaySeconds: { min: 0, max: 600 },
  /// Per-probe HTTP timeout. 2min ceiling — beyond that the app is wedged.
  timeoutSeconds: { min: 1, max: 120 },
  /// Gap between probes. 5min ceiling keeps detection responsive.
  intervalSeconds: { min: 1, max: 300 },
  /// Consecutive successes before a deployment is declared HEALTHY.
  healthyThreshold: { min: 1, max: 10 },
  /// Consecutive failures before a running instance is declared unhealthy
  /// (drives rollback). Both thresholds capped at 10 — beyond that the
  /// probe is just slow to react.
  unhealthyThreshold: { min: 1, max: 10 },
  /// Consecutive FAILED post-deploy smoke rounds before the platform pulls an
  /// already-serving deployment. Same shape and the same cap as
  /// `unhealthyThreshold` above, one level up: that one counts failed probes of
  /// the CONTAINER before declaring it unhealthy, this one counts failed drives
  /// of the OPERATOR'S OWN ROUTES before undoing a deploy that already took
  /// traffic. Capped at 10 for the same reason — beyond that the gate is just
  /// slow to react — and floored at 1, which is "pull on the first wrong
  /// answer".
  smokeFailureThreshold: { min: 1, max: 10 }
} as const;

/**
 * The flap threshold applied when an app has not set one.
 *
 * Deliberately equal to `VIBE_APP_HEALTH_CHECK_DEFAULTS.unhealthyThreshold`: the
 * platform already answers "how many consecutive failures before we act?" with
 * 3 one level down, and giving the same question two different answers in one
 * config object is how an operator ends up reasoning about the wrong one. The
 * number itself is not measured — no data exists yet on how often a real smoke
 * fails transiently — so matching the neighbouring knob is the honest default
 * rather than a tuned one.
 */
export const VIBE_APP_SMOKE_FAILURE_THRESHOLD_DEFAULT = 3;

/**
 * How long a deployment may sit in DEPLOYING before the control plane gives up
 * and reaps it to FAILED — the OUTER clock, distinct from every threshold
 * above.
 *
 * The bounds above describe the probe loop: how many seconds of probing it
 * takes to call a version healthy or pull a failing one. This is the different
 * question of how long the platform waits for the executor to report anything
 * at all, and it is the window named in "the new version didn't report healthy
 * inside the rollout window".
 *
 * It lives here, beside the thresholds, because the console and the reaper were
 * each holding their own idea of it: Settings derived "a failing version is
 * pulled after ~30s" from the probe loop while a real timeout said 15 minutes,
 * and a reader had no way to see those were two clocks rather than one number
 * that disagreed with itself by 30×. One exported constant means the sentence
 * the console prints and the deadline the backend enforces cannot drift.
 */
export const VIBE_DEPLOYMENT_HEALTHY_BUDGET_MS = 15 * 60 * 1000;

export const VibeAppHealthCheckConfigSchema = z.object({
  path: z.string().startsWith("/").max(VIBE_APP_HEALTH_CHECK_BOUNDS.path.maxLength),
  port: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.port.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.port.max)
    .nullable(),
  initialDelaySeconds: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.initialDelaySeconds.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.initialDelaySeconds.max),
  timeoutSeconds: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.timeoutSeconds.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.timeoutSeconds.max),
  intervalSeconds: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.intervalSeconds.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.intervalSeconds.max),
  healthyThreshold: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.healthyThreshold.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.healthyThreshold.max),
  unhealthyThreshold: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.unhealthyThreshold.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.unhealthyThreshold.max),
  /// Routes the operator names for the post-deploy smoke — see
  /// `post-deploy-smoke.schema.ts`. Empty is the default and means the platform
  /// asserts only what it always has: the app's health path answered.
  ///
  /// It rides in this object, and therefore in the existing
  /// `healthCheckConfigJson` column, because it is per-app probe configuration
  /// and because `.default([])` makes every row that predates it parse to the
  /// empty list — so no column, no DDL and no backfill are involved.
  ///
  /// 🔴 It is NOT the same probe. `path` above is driven by the tenant agent
  /// against the CONTAINER's own port before the deployment flips HEALTHY;
  /// these are driven by the control plane against the app's PUBLIC URL after
  /// the edge is proved to be serving the new build. Smoking at the healthy
  /// flip would exercise whatever the edge still had, which is the previous
  /// build — the exact lag `DEPLOYMENT_SERVED` exists to measure.
  ///
  /// 🔴 `.optional()`, NOT `.default([])`, and the difference is not stylistic.
  /// A Zod default makes the OUTPUT type required, which invalidates every
  /// hand-written `VibeAppHealthCheckConfig` object literal in the repo — five
  /// of them live in `apps/vibe-agent` test files, and more exist in fixtures
  /// this package cannot see. Optional keeps the field strictly ADDITIVE: no
  /// existing literal changes, and the empty list is supplied by the one place
  /// that reads it. Absent and empty mean the same thing here — nothing
  /// declared, nothing asserted — so there is no state the narrower type loses.
  smokeRoutes: VibeAppSmokeRoutesSchema.optional(),
  /// How many CONSECUTIVE failed smoke rounds a declared route must produce
  /// before the platform pulls an already-serving deployment. Absent means
  /// {@link VIBE_APP_SMOKE_FAILURE_THRESHOLD_DEFAULT}.
  ///
  /// 🔴 `.optional()`, NOT `.default()`, for exactly the reason `smokeRoutes`
  /// above carries: a Zod default makes the OUTPUT type required, which
  /// invalidates every hand-written `VibeAppHealthCheckConfig` object literal in
  /// the repo — several live in `apps/vibe-agent` test files and more in
  /// fixtures this package cannot see. Optional keeps the field strictly
  /// ADDITIVE, and the default is supplied by the one place that reads it.
  ///
  /// It is deliberately absent from the schema.prisma `@default(...)` literal
  /// too: an app that never tuned it simply does not carry the key, so adding it
  /// to the DDL would be a migration that changes no behaviour.
  ///
  /// 🔴 A threshold of 1 is "pull on the first wrong answer" and is a REAL
  /// setting, not a disabled one. There is no off switch here on purpose —
  /// an app declares smoke routes to have them enforced, and an operator who
  /// wants no gate removes the route rather than setting a threshold nothing
  /// can reach.
  smokeFailureThreshold: z
    .number()
    .int()
    .min(VIBE_APP_HEALTH_CHECK_BOUNDS.smokeFailureThreshold.min)
    .max(VIBE_APP_HEALTH_CHECK_BOUNDS.smokeFailureThreshold.max)
    .optional()
});

export type VibeAppHealthCheckConfig = z.infer<typeof VibeAppHealthCheckConfigSchema>;

/** The health-check config as a reader parses it: its smoke routes through their read twin. */
export const VibeAppHealthCheckConfigReadSchema = VibeAppHealthCheckConfigSchema.extend({
  smokeRoutes: VibeAppSmokeRoutesReadSchema.optional()
});

/**
 * Defaults applied at `VibeApp.create()` and as the fallback for legacy
 * rows whose `healthCheckConfigJson` doesn't parse. The DB-level
 * `@default` on the column ensures backfill on migration; this constant
 * is the source of truth for the in-process default.
 *
 * Every REQUIRED key the schema declares must appear in the schema.prisma
 * `@default(...)` literal, because nothing else supplies one. `smokeRoutes` is
 * optional and is deliberately absent from that literal: an app that has never
 * declared a route simply does not carry the key, so adding it to the DDL would
 * be a migration that changes no behaviour.
 *
 * Sizing rationale: a root-path probe with a short delay/interval matches
 * Railway / Render "starter" defaults so a fresh app reports healthy
 * without operator tuning. Operators tune these per-app via the admin
 * PATCH endpoint when an app needs a dedicated `/healthz` or a longer
 * boot grace period.
 */
export const VIBE_APP_HEALTH_CHECK_DEFAULTS: VibeAppHealthCheckConfig = {
  path: "/",
  port: null,
  initialDelaySeconds: 5,
  timeoutSeconds: 5,
  intervalSeconds: 10,
  healthyThreshold: 2,
  unhealthyThreshold: 3
};
